import { useMemo } from "react";
import { Badge, Box, Group, Text, Tooltip } from "@mantine/core";
import { IconFlask } from "@tabler/icons-react";
import {
  DeleteStainButton,
  PATHOLOGIST_REVIEW_STAIN_DELETE_PERMISSIONS,
} from "../workflows";
import type { PendingStainInfo } from "../../services/stainRequest.service";

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays}d ago`;
  if (diffHours > 0) return `${diffHours}h ago`;
  if (diffMins > 0) return `${diffMins}m ago`;
  return "Just now";
}

function getCaseStatusLabel(status: string): string {
  switch (status) {
    case "Pending Distribution":
      return "Ready";
    default:
      return status;
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "ready_for_staining":
      return "Ready";
    case "staining_in_progress":
      return "In Progress";
    case "staining_completed":
      return "Done";
    case "shipped":
      return "Shipped";
    case "ready_for_review":
      return "Ready for Review";
    default:
      return status;
  }
}

interface Props {
  stain: PendingStainInfo;
  caseId: string;
  caseNumber?: string;
}

export function PendingStainItem({ stain, caseId, caseNumber }: Props) {
  const statusLabel = useMemo(
    () =>
      stain.childCase
        ? getCaseStatusLabel(stain.childCase.status)
        : getStatusLabel(stain.status),
    [stain.childCase, stain.status]
  );

  const isAutoLinked = !!stain.autoLinkedDiagnosisId;

  // An auto-linked stain is owned by the diagnosis macro that ordered it —
  // deleting the slide here would leave the macro to re-create it.
  const canDelete = useMemo(
    () =>
      !isAutoLinked &&
      (stain.status === "pending" ||
        stain.status === "ready_for_staining" ||
        stain.childCase?.status === "Pending Cutting"),
    [isAutoLinked, stain.status, stain.childCase]
  );

  const slide = useMemo(
    () => ({
      id: stain.id,
      status: stain.status,
      panelOrderComponentId: stain.panelOrderComponentId,
      stain: { code: stain.stain.code },
    }),
    [stain.id, stain.status, stain.panelOrderComponentId, stain.stain.code]
  );

  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start">
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" fw={500}>
            {stain.stain.code}
          </Text>
          {isAutoLinked && (
            <Tooltip
              label="Auto-ordered via diagnosis macro. Remove the macro to cancel."
              withArrow
              maw={220}
              multiline
            >
              <Badge
                size="xs"
                variant="light"
                color="teal"
                leftSection={<IconFlask size={9} />}
                style={{ cursor: "default" }}
              >
                Auto-linked
              </Badge>
            </Tooltip>
          )}
          {stain.levelsRequested && (
            <Text size="xs" c="dimmed">
              x{stain.levelsRequested === "x3" ? "3" : "1"}
            </Text>
          )}
          <Text size="xs" c="dimmed">
            · {statusLabel}
          </Text>
        </Group>
        <Text size="xs" c="dimmed">
          Part {stain.specimen.number}, Block {stain.block.number}
          {stain.provider.isInHouse ? "" : ` · ${stain.provider.name}`}
          {" · "}
          {formatTimeAgo(stain.requestedAt)}
        </Text>
      </Box>

      {canDelete && (
        <DeleteStainButton
          slide={slide}
          caseId={caseId}
          caseNumber={caseNumber}
          slideLabel={`${stain.specimen.number}-${stain.block.number}`}
          requiredPermissions={PATHOLOGIST_REVIEW_STAIN_DELETE_PERMISSIONS}
        />
      )}
    </Group>
  );
}
